import { Component, computed, HostListener, inject, signal } from '@angular/core';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { SafeUrl } from '@angular/platform-browser';
import { ButtonDirective, DialogCloseButtonComponent } from '@innersource/common-ui-components';
import { DynamicTableComponent } from '../../../../utils/dynamic-table/dynamic-table.component';
import { GraphComponent } from '../../../../utils/graph/graph.component';
import { valueText } from '../../job-timeline/timeline-preview/timeline-preview.util';
import { ToolJobResultItem } from '../tool-job-results/tool-job-results.types';

type ToolJobPreviewDialogData = {
  items: ToolJobResultItem[];
  initialIndex: number;
  contextTitle: string;
  content: 'results' | 'parameters';
};

type PreviewDownload = {
  fileName: string;
  url: SafeUrl;
};

@Component({
  selector: 'app-tool-job-preview-dialog',
  templateUrl: './tool-job-preview-dialog.component.html',
  styleUrls: ['./tool-job-preview-dialog.component.scss'],
  imports: [ButtonDirective, DialogCloseButtonComponent, DynamicTableComponent, GraphComponent],
})
export class ToolJobPreviewDialogComponent {
  /** Template Forwards. */
  readonly valueText = valueText;

  /** State. */
  readonly data = inject<ToolJobPreviewDialogData>(DIALOG_DATA);
  readonly currentIndex = signal(Math.max(0, this.data.initialIndex));

  /** Returns the currently selected preview item. */
  readonly currentItem = computed(() => this.data.items[this.currentIndex()]);

  /** Returns download metadata for the current item. */
  readonly currentDownload = computed(() => this.downloadFor(this.currentItem()));

  /** Shows the previous preview item. */
  previous(): void {
    if (this.data.items.length < 2) return;
    this.currentIndex.update(
      index => (index - 1 + this.data.items.length) % this.data.items.length
    );
  }

  /** Shows the next preview item. */
  next(): void {
    if (this.data.items.length < 2) return;
    this.currentIndex.update(index => (index + 1) % this.data.items.length);
  }

  /** Checks whether an item can be downloaded. */
  canDownload(item: ToolJobResultItem): boolean {
    return Boolean(this.downloadFor(item));
  }

  /** Returns the reason an item cannot be downloaded. */
  downloadUnavailableMessage(item: ToolJobResultItem): string | undefined {
    if (item.kind !== 'image' && item.kind !== 'text' && item.kind !== 'file') return undefined;
    if (this.canDownload(item)) return undefined;
    return this.data.content === 'parameters'
      ? 'Input files are not available for download.'
      : 'Downloads are disabled for this file.';
  }

  /** Returns download metadata for an item. */
  private downloadFor(item: ToolJobResultItem): PreviewDownload | undefined {
    if (item.kind === 'image' && item.allowDownload) {
      return { fileName: item.fileName, url: item.src };
    }
    if ((item.kind === 'text' || item.kind === 'file') && item.src) {
      return { fileName: item.fileName, url: item.src };
    }
    return undefined;
  }

  /** Shows the previous item from the keyboard. */
  @HostListener('document:keydown.arrowleft')
  previousFromKeyboard(): void {
    this.previous();
  }

  /** Shows the next item from the keyboard. */
  @HostListener('document:keydown.arrowright')
  nextFromKeyboard(): void {
    this.next();
  }
}
